import { callT } from '~/utils/callI18n';
import { ICE_SERVERS, PUBLISHER_OFFER_OPTIONS } from './constants';
import { markNetworkRecovered } from './networkDrop';
import type { JanusCallRefs } from './sessionRefs';
import type { SendJanusFn, VideoRoomPublisher } from './types';
import {
  drainPendingCandidates,
  finishJoinIfPublisherReady,
  hasLiveLocalMic,
  logJanus,
  parsePublisherFeedRef,
} from './utils';

function publisherMediaFlags(r: JanusCallRefs): {
  audio: boolean;
  video: false;
  muted: boolean;
} {
  const hasMic = hasLiveLocalMic(r);
  return {
    audio: hasMic,
    video: false,
    muted: r.isMutedRef.current || !hasMic,
  };
}

export interface PublisherDeps {
  sendJanus: SendJanusFn;
  sendPublisherMessage: (
    body: Record<string, unknown>,
    jsep?: RTCSessionDescriptionInit,
    transaction?: string
  ) => void;
  subscribeToPublishersFn: (publishers?: VideoRoomPublisher[]) => void;
  rejectPending: (message: string) => void;
  cleanupCall: () => void;
  cancelWebSocketReconnect: () => void;
}

export function bindPublisherConnectionMonitor(
  r: JanusCallRefs,
  pc: RTCPeerConnection,
  isActiveCallSession: () => boolean
): void {
  const onHealthy = () => {
    r.mediaConnectionLostSignaledRef.current = false;
    r.wsReconnectAttemptsRef.current = 0;
  };

  pc.onconnectionstatechange = () => {
    const state = pc.connectionState;
    if (state === 'connected') {
      onHealthy();
      return;
    }
    if (!isActiveCallSession()) return;
    if (state === 'failed' || state === 'disconnected') {
      r.scheduleWebSocketReconnectRef.current();
    }
  };

  pc.oniceconnectionstatechange = () => {
    const iceState = pc.iceConnectionState;
    if (iceState === 'connected' || iceState === 'completed') {
      onHealthy();
      r.publisherIceConnectedNotifyRef.current?.();
      return;
    }
    if (!isActiveCallSession()) return;
    if (iceState === 'disconnected' || iceState === 'failed') {
      r.scheduleWebSocketReconnectRef.current();
    }
  };
}

export function releasePublisherPeerConnection(r: JanusCallRefs): void {
  const pc = r.publisherPcRef.current;
  if (!pc) return;

  pc.onconnectionstatechange = null;
  pc.oniceconnectionstatechange = null;
  pc.onicecandidate = null;
  pc.ontrack = null;

  try {
    pc.getSenders().forEach((sender) => {
      if (sender.track) {
        pc.removeTrack(sender);
      }
    });
  } catch {
    
  }

  if (pc.signalingState !== 'closed') {
    pc.close();
  }
  r.publisherPcRef.current = null;
  r.publisherPendingCandidatesRef.current = [];
  r.isPublisherConfiguredRef.current = false;
  logJanus('RECONNECT', 'released publisher PeerConnection');
}

export function rebuildPublisherPeerConnection(
  r: JanusCallRefs,
  sendJanus: SendJanusFn,
  isActiveCallSession: () => boolean
): boolean {
  const stream = r.localStreamRef.current;
  if (!stream) return false;

  releasePublisherPeerConnection(r);

  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
  bindPublisherConnectionMonitor(r, pc, isActiveCallSession);
  r.publisherPcRef.current = pc;

  stream.getAudioTracks().forEach((track) => {
    pc.addTrack(track, stream);
  });

  pc.onicecandidate = (e) => {
    if (r.sessionIdRef.current && r.handleIdRef.current) {
      sendJanus({
        janus: 'trickle',
        session_id: r.sessionIdRef.current,
        handle_id: r.handleIdRef.current,
        candidate: e.candidate ? e.candidate : { completed: true },
      });
    }
  };

  r.isPublisherConfiguredRef.current = false;
  return true;
}

export async function sendPublisherJoin(
  r: JanusCallRefs,
  sendPublisherMessage: PublisherDeps['sendPublisherMessage'],
  transaction: string,
  withSdpOffer: boolean
): Promise<void> {
  const body: Record<string, unknown> = {
    request: 'join',
    room: r.roomIdRef.current,
    ptype: 'publisher',
    display: r.displayNameRef.current,
    ...publisherMediaFlags(r),
  };
  if (r.displayNameRef.current) {
    body.id = r.displayNameRef.current;
  }

  let jsep: RTCSessionDescriptionInit | undefined;
  if (withSdpOffer && r.publisherPcRef.current) {
    const offer = await r.publisherPcRef.current.createOffer(PUBLISHER_OFFER_OPTIONS);
    await r.publisherPcRef.current.setLocalDescription(offer);
    jsep = offer;
  }

  sendPublisherMessage(body, jsep, transaction);
}

function remotePublishersInRoom(
  r: JanusCallRefs,
  publishers: VideoRoomPublisher[] | undefined
): VideoRoomPublisher[] {
  if (!publishers?.length) return [];
  const myId = r.myPublisherIdRef.current;
  return publishers.filter((p) => p.id !== myId && !r.subscriberFeedsRef.current.some((s) => s.feed === p.id));
}

export async function ensureCalleeCanHearPublisher(
  r: JanusCallRefs,
  set: import('./sessionRefs').JanusCallSetters,
  sendPublisherMessage: PublisherDeps['sendPublisherMessage'],
  reason: string
): Promise<void> {
  if (!r.isPublisherJoinedRef.current || !r.publisherPcRef.current) {
    finishJoinIfPublisherReady(r, set);
    return;
  }

  if (r.isPublisherConfiguredRef.current) {
    const now = Date.now();
    if (reason === 'ice-connected' && now - r.lastRepublishOutboundAtRef.current < 1500) {
      finishJoinIfPublisherReady(r, set);
      return;
    }
    r.lastRepublishOutboundAtRef.current = now;

    logJanus('PUB', `ensure outbound to room (${reason})`, { myId: r.myPublisherIdRef.current });

    try {
      await republishPublisherAudio(r, sendPublisherMessage);
    } catch (err) {
      logJanus('PUB', 'republish for callee failed', err);
      sendPublisherMessage(
        { request: 'configure', ...publisherMediaFlags(r) },
        undefined,
        'configure-outbound-fallback'
      );
    }

    sendPublisherMessage({ request: 'listparticipants' }, undefined, 'list-participants-outbound');
  } else {
    logJanus('PUB', `defer outbound republish (${reason}) — publisher SDP not ready`);
  }

  finishJoinIfPublisherReady(r, set);
}

export async function republishPublisherAudio(
  r: JanusCallRefs,
  sendPublisherMessage: PublisherDeps['sendPublisherMessage']
): Promise<boolean> {
  const pc = r.publisherPcRef.current;
  if (!pc || pc.connectionState === 'closed') return false;

  const now = Date.now();
  if (now - r.lastRepublishOutboundAtRef.current < 1500) {
    return true;
  }
  r.lastRepublishOutboundAtRef.current = now;

  const offer = await pc.createOffer(PUBLISHER_OFFER_OPTIONS);
  await pc.setLocalDescription(offer);

  sendPublisherMessage(
    {
      request: 'configure',
      ...publisherMediaFlags(r),
    },
    offer,
    'configure-mic-recover'
  );
  logJanus('PUB', 'republish configure after mic recover');
  return true;
}

export async function startPublisherWebRTC(
  r: JanusCallRefs,
  sendPublisherMessage: PublisherDeps['sendPublisherMessage']
): Promise<void> {
  if (!r.publisherPcRef.current || r.isPublisherConfiguredRef.current) return;

  const pc = r.publisherPcRef.current;
  const offer = await pc.createOffer(PUBLISHER_OFFER_OPTIONS);
  await pc.setLocalDescription(offer);

  sendPublisherMessage(
    {
      request: 'configure',
      ...publisherMediaFlags(r),
    },
    offer,
    'configure-pub'
  );
  logJanus('PUB', 'configure sent — waiting answer', { muted: r.isMutedRef.current });
}

export async function handlePublisherEvent(
  r: JanusCallRefs,
  set: import('./sessionRefs').JanusCallSetters,
  data: Record<string, unknown>,
  pluginBody: Record<string, unknown>,
  deps: PublisherDeps
): Promise<void> {
  const videoroom = pluginBody.videoroom as string | undefined;
  const pluginError = pluginBody.error as string | undefined;
  const jsep = data.jsep as RTCSessionDescriptionInit | undefined;

  if (pluginError) {
    deps.rejectPending(pluginError);
    deps.cleanupCall();
    return;
  }

  if (videoroom === 'participants') {
    const participants = pluginBody.participants as VideoRoomPublisher[] | undefined;
    deps.subscribeToPublishersFn(participants);
    return;
  }

  if (videoroom === 'joined') {
    r.isPublisherJoinedRef.current = true;

    const joinedPublisherId = pluginBody.id;
    if (joinedPublisherId !== undefined && joinedPublisherId !== null && joinedPublisherId !== '') {
      r.myPublisherIdRef.current =
        typeof joinedPublisherId === 'number' || typeof joinedPublisherId === 'string'
          ? joinedPublisherId
          : null;
    }

    const joinedPrivateId = pluginBody.private_id;
    if (typeof joinedPrivateId === 'number') {
      r.myPrivateIdRef.current = joinedPrivateId;
    }

    const isMediaReconnect = r.mediaReconnectPendingRef.current;
    if (isMediaReconnect) {
      r.mediaReconnectPendingRef.current = false;
      r.pendingLocalStreamRefreshRef.current = false;
      r.micDisconnectHandledRef.current = false;
      markNetworkRecovered(r);
      r.subscribedFeedIdsRef.current.clear();
      if (r.myPublisherIdRef.current !== null) {
        r.subscribedFeedIdsRef.current.add(r.myPublisherIdRef.current);
      }
    } else if (r.myPublisherIdRef.current !== null) {
      r.subscribedFeedIdsRef.current.add(r.myPublisherIdRef.current);
    }

    const publishers = pluginBody.publishers as VideoRoomPublisher[] | undefined;
    logJanus('PUB', 'joined', {
      myId: r.myPublisherIdRef.current,
      myPrivateId: r.myPrivateIdRef.current,
      publishers,
      hasJsep: !!jsep,
      mediaReconnect: isMediaReconnect,
    });

    if (jsep?.type === 'answer' && r.publisherPcRef.current) {
      try {
        await r.publisherPcRef.current.setRemoteDescription(new RTCSessionDescription(jsep));
        await drainPendingCandidates(r.publisherPcRef.current, r.publisherPendingCandidatesRef.current);
        r.isPublisherConfiguredRef.current = true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : callT('errors.publisherSdp', r.translateCallRef.current);
        deps.rejectPending(message);
        deps.cleanupCall();
        return;
      }
    }

    deps.subscribeToPublishersFn(publishers);

    if (isMediaReconnect || !publishers?.length) {
      deps.sendPublisherMessage({ request: 'listparticipants' }, undefined, 'list-participants');
    }

    if (!r.isPublisherConfiguredRef.current) {
      await startPublisherWebRTC(r, deps.sendPublisherMessage);
    }

    const remotesOnJoin = remotePublishersInRoom(r, publishers);
    if (remotesOnJoin.length > 0) {
      await ensureCalleeCanHearPublisher(r, set, deps.sendPublisherMessage, 'joined-with-remote');
    }
    finishJoinIfPublisherReady(r, set);
    deps.cancelWebSocketReconnect();
    return;
  }

  if (jsep?.type === 'answer' && r.publisherPcRef.current) {
    try {
      await r.publisherPcRef.current.setRemoteDescription(new RTCSessionDescription(jsep));
      await drainPendingCandidates(r.publisherPcRef.current, r.publisherPendingCandidatesRef.current);
      if (!r.isPublisherConfiguredRef.current) {
        r.isPublisherConfiguredRef.current = true;
      }
      finishJoinIfPublisherReady(r, set);
      r.mediaConnectionLostSignaledRef.current = false;
      r.wsReconnectAttemptsRef.current = 0;
      deps.cancelWebSocketReconnect();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : callT('errors.publisherSdp', r.translateCallRef.current);
      deps.rejectPending(message);
      deps.cleanupCall();
    }
    return;
  }

  if (videoroom === 'event') {
    const publishers = pluginBody.publishers as VideoRoomPublisher[] | undefined;
    const newRemotes = remotePublishersInRoom(r, publishers);
    if (publishers?.length) {
      deps.subscribeToPublishersFn(publishers);
    }
    if (newRemotes.length > 0) {
      logJanus('PUB', 'remote joined room — republish outbound', newRemotes);
      await ensureCalleeCanHearPublisher(r, set, deps.sendPublisherMessage, 'remote-joined');
    }

    if (pluginBody.configured === 'ok') {
      finishJoinIfPublisherReady(r, set);
    }

    const leavingId = parsePublisherFeedRef(pluginBody.leaving);
    const unpublishedId = parsePublisherFeedRef(pluginBody.unpublished);

    const removeRemoteFeed = (feedId: number | string, reason: 'leaving' | 'unpublished') => {
      const isRemote = r.subscriberFeedsRef.current.some((s) => s.feed === feedId);
      if (!isRemote) return;

      logJanus('PUB', `remote publisher ${feedId} ${reason}`);
      r.subscriberFeedsRef.current.forEach((sub) => {
        if (sub.feed === feedId) sub.pc?.close();
      });
      r.subscriberFeedsRef.current = r.subscriberFeedsRef.current.filter((s) => s.feed !== feedId);
      r.subscribedFeedIdsRef.current.delete(feedId);
      set.setRemoteStream(null);

      if (r.subscriberFeedsRef.current.length === 0) {
        logJanus('PUB', 'no remote subscribers — remote call ended');
        r.onRemoteCallEndedRef.current?.();
      }
    };

    if (leavingId !== undefined) {
      removeRemoteFeed(leavingId, 'leaving');
    }
    if (unpublishedId !== undefined) {
      removeRemoteFeed(unpublishedId, 'unpublished');
    }
    return;
  }

  if (videoroom === 'destroyed') {
    deps.cleanupCall();
    r.onRemoteCallEndedRef.current?.();
  }
}
